/**
 * Compact high-signal activity → short "recent intent" string for board AI.
 * Persist only the summary; the in-memory ring is session-local.
 */

export const RECENT_INTENT_MAX = 240;
export const INTENT_LABEL_MAX = 40;
export const INTENT_EVENT_RING_MAX = 20;

export const INTENT_EVENT_TYPES = Object.freeze({
  PIN_ADDED: 'pin_added',
  PIN_REMOVED: 'pin_removed',
  GOAL_CHANGED: 'goal_changed',
  DESCRIPTION_CHANGED: 'description_changed',
  REC_DISMISSED: 'rec_dismissed',
  GAP_NOT_INTERESTED: 'gap_not_interested',
  GAP_INTERESTED: 'gap_interested',
  SELECTION_FOCUS: 'selection_focus',
  FOCUS_CLEARED: 'focus_cleared',
  DECIDED: 'decided',
  ASKED: 'asked',
  ASK_CLEARED: 'ask_cleared',
  STEP_DROPPED: 'step_dropped',
});

/** @type {Map<string, { events: Array, seededSummary: string }>} */
const buffersByHashtag = new Map();

function bufferKey(hashtag) {
  return String(hashtag || '').trim() || '__default__';
}

function getBuffer(hashtag) {
  const key = bufferKey(hashtag);
  let entry = buffersByHashtag.get(key);
  if (!entry) {
    entry = { events: [], seededSummary: '' };
    buffersByHashtag.set(key, entry);
  }
  return entry;
}

export function truncateIntentLabel(text, max = INTENT_LABEL_MAX) {
  const trimmed = String(text || '').trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, Math.max(1, max - 1))}…`;
}

export function clampRecentIntent(text) {
  const trimmed = String(text || '').trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';
  if (trimmed.length <= RECENT_INTENT_MAX) return trimmed;
  return `${trimmed.slice(0, Math.max(1, RECENT_INTENT_MAX - 1))}…`;
}

/**
 * Collapse consecutive same-type events; keep only the latest selection_focus.
 */
export function coalesceIntentEvents(events = []) {
  const out = [];
  for (const raw of events) {
    if (!raw || !raw.type) continue;
    const type = String(raw.type);
    const label = truncateIntentLabel(raw.label);
    const at = Number(raw.at) || Date.now();
    const event = { type, label, at };

    if (
      type === INTENT_EVENT_TYPES.SELECTION_FOCUS
      || type === INTENT_EVENT_TYPES.FOCUS_CLEARED
    ) {
      const idx = out.findIndex(e => (
        e.type === INTENT_EVENT_TYPES.SELECTION_FOCUS
        || e.type === INTENT_EVENT_TYPES.FOCUS_CLEARED
      ));
      if (idx >= 0) out.splice(idx, 1);
      if (type === INTENT_EVENT_TYPES.FOCUS_CLEARED || label) out.push(event);
      continue;
    }
    if (type === INTENT_EVENT_TYPES.ASKED || type === INTENT_EVENT_TYPES.ASK_CLEARED) {
      const idx = out.findIndex(e => (
        e.type === INTENT_EVENT_TYPES.ASKED
        || e.type === INTENT_EVENT_TYPES.ASK_CLEARED
      ));
      if (idx >= 0) out.splice(idx, 1);
      out.push(event);
      continue;
    }

    const last = out[out.length - 1];
    if (last && last.type === type) {
      out[out.length - 1] = event;
      continue;
    }
    out.push(event);
  }
  return out.slice(-INTENT_EVENT_RING_MAX);
}

function phraseForEvent(event) {
  const label = truncateIntentLabel(event?.label);
  const quoted = label ? `"${label}"` : '';
  switch (event?.type) {
    case INTENT_EVENT_TYPES.PIN_ADDED:
      return label ? `Added ${quoted}` : 'Added a pin';
    case INTENT_EVENT_TYPES.PIN_REMOVED:
      return label ? `Removed ${quoted}` : 'Removed a pin';
    case INTENT_EVENT_TYPES.GOAL_CHANGED:
      return label ? `Set current step to ${quoted}` : 'Cleared current step';
    case INTENT_EVENT_TYPES.DESCRIPTION_CHANGED:
      return label ? `Updated board goal to ${quoted}` : 'Cleared board goal';
    case INTENT_EVENT_TYPES.REC_DISMISSED:
      return label ? `Dismissed ${quoted}` : 'Dismissed a suggestion';
    case INTENT_EVENT_TYPES.GAP_NOT_INTERESTED:
      return label ? `Not interested in ${quoted}` : 'Not interested in a gap';
    case INTENT_EVENT_TYPES.GAP_INTERESTED:
      return label ? `Interested again in ${quoted}` : 'Interested again in a gap';
    case INTENT_EVENT_TYPES.DECIDED:
      return label ? `Decided ${quoted}` : 'Recorded a decision';
    case INTENT_EVENT_TYPES.SELECTION_FOCUS:
      return label ? `Focusing ${label}` : '';
    case INTENT_EVENT_TYPES.FOCUS_CLEARED:
      return label ? `Stopped focusing ${quoted}` : 'Stopped focusing';
    case INTENT_EVENT_TYPES.ASKED:
      return label ? `Asked ${quoted}` : 'Asked a question';
    case INTENT_EVENT_TYPES.ASK_CLEARED:
      return label ? `Stopped asking ${quoted}` : 'Cleared an ask';
    case INTENT_EVENT_TYPES.STEP_DROPPED:
      return label ? `Dropped step ${quoted}` : 'Dropped a step';
    default:
      return label || '';
  }
}

/**
 * Build a compact summary from events + optional prior persisted summary.
 */
export function summarizeRecentIntent(events = [], { priorSummary = '' } = {}) {
  const coalesced = coalesceIntentEvents(events);
  const phrases = coalesced.map(phraseForEvent).filter(Boolean);
  const prior = clampRecentIntent(priorSummary);
  if (!phrases.length) return prior;

  const joined = phrases.join('; ');
  if (!prior) return clampRecentIntent(joined);

  // Prefer fresh phrases; keep a short prior tail if room remains.
  const fresh = clampRecentIntent(joined);
  if (fresh.length >= RECENT_INTENT_MAX - 20) return fresh;
  const room = RECENT_INTENT_MAX - fresh.length - 2;
  if (room < 24) return fresh;
  const priorTail = prior.length <= room ? prior : `…${prior.slice(-(room - 1))}`;
  return clampRecentIntent(`${fresh}; ${priorTail}`);
}

export function seedBoardIntentFromSummary(hashtag, priorSummary) {
  const entry = getBuffer(hashtag);
  entry.seededSummary = clampRecentIntent(priorSummary);
  // Keep pending events; only seed when empty so remounts do not wipe session signals.
  if (!entry.events.length && entry.seededSummary) {
    // seededSummary alone is enough until new events arrive
  }
}

export function pushBoardIntentEvent(hashtag, event) {
  if (!event?.type) return getBuffer(hashtag).events.length;
  const entry = getBuffer(hashtag);
  const next = {
    type: String(event.type),
    label: truncateIntentLabel(event.label),
    at: Number(event.at) || Date.now(),
  };
  if (
    next.type === INTENT_EVENT_TYPES.SELECTION_FOCUS
    || next.type === INTENT_EVENT_TYPES.FOCUS_CLEARED
  ) {
    entry.events = entry.events.filter(e => (
      e.type !== INTENT_EVENT_TYPES.SELECTION_FOCUS
      && e.type !== INTENT_EVENT_TYPES.FOCUS_CLEARED
    ));
  }
  if (
    next.type === INTENT_EVENT_TYPES.ASKED
    || next.type === INTENT_EVENT_TYPES.ASK_CLEARED
  ) {
    entry.events = entry.events.filter(e => (
      e.type !== INTENT_EVENT_TYPES.ASKED
      && e.type !== INTENT_EVENT_TYPES.ASK_CLEARED
    ));
  }
  const last = entry.events[entry.events.length - 1];
  if (
    last
    && last.type === next.type
    && next.type !== INTENT_EVENT_TYPES.SELECTION_FOCUS
    && next.type !== INTENT_EVENT_TYPES.FOCUS_CLEARED
  ) {
    entry.events[entry.events.length - 1] = next;
  } else {
    entry.events.push(next);
  }
  if (entry.events.length > INTENT_EVENT_RING_MAX) {
    entry.events = entry.events.slice(-INTENT_EVENT_RING_MAX);
  }
  return entry.events.length;
}

/**
 * Build current summary without clearing the ring (for semantic key / fingerprint).
 */
export function getBoardRecentIntentSummary(hashtag, { priorSummary } = {}) {
  const entry = getBuffer(hashtag);
  const prior = priorSummary != null ? priorSummary : entry.seededSummary;
  return summarizeRecentIntent(entry.events, { priorSummary: prior });
}

/**
 * Drain pending events into an updated summary and keep that as the new seed.
 * Clears the ring after fold so the next session delta is net-new.
 */
export function foldBoardIntentSummary(hashtag, { priorSummary } = {}) {
  const entry = getBuffer(hashtag);
  const prior = priorSummary != null ? priorSummary : entry.seededSummary;
  const summary = summarizeRecentIntent(entry.events, { priorSummary: prior });
  entry.events = [];
  entry.seededSummary = summary;
  return summary;
}

/** Test helper */
export function resetBoardIntentBuffers() {
  buffersByHashtag.clear();
}
